import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

import { v4 as uuidv4 } from "uuid";
import {
    CalendarDays,
    Coins,
    AlertTriangle,
    Save,
    CreditCard,
} from "lucide-react";

export interface PaymentData {
    id: string;
    date: string;
    amount: number;
    type?: "payment" | "discount";
}

interface PaymentPopupProps {
    open: boolean;
    onClose: () => void;
    onSave: (payment: PaymentData) => void;
    initialData?: PaymentData;
    maxAmount?: number;
}

const PaymentPopup: React.FC<PaymentPopupProps> = ({
                                                       open,
                                                       onClose,
                                                       onSave,
                                                       initialData,
                                                       maxAmount,
                                                   }) => {
    const [paymentData, setPaymentData] = useState<PaymentData>(
        initialData || {
            id: uuidv4(),
            date: new Date().toISOString().split("T")[0],
            amount: 0,
            type: "payment",
        }
    );

    const [errors, setErrors] = useState<{ date?: string; amount?: string }>({});

    useEffect(() => {
        if (open) {
            setErrors({});
            if (initialData) {
                setPaymentData(initialData);
            } else {
                setPaymentData({
                    id: uuidv4(),
                    date: new Date().toISOString().split("T")[0],
                    amount: 0,
                    type: "payment",
                });
            }
        }
    }, [open, initialData]);

    const handleChange =
        (field: keyof PaymentData) => (e: React.ChangeEvent<HTMLInputElement>) => {
            setPaymentData((prev) => ({
                ...prev,
                [field]: field === "amount" ? Number(e.target.value) : e.target.value,
            }));
        };

    const validate = () => {
        const newErrors: { date?: string; amount?: string } = {};

        if (!paymentData.date) {
            newErrors.date = "Дата обязательна";
        }

        if (!paymentData.amount || paymentData.amount <= 0) {
            newErrors.amount = "Сумма должна быть больше 0";
        }

        if (maxAmount !== undefined && paymentData.amount > maxAmount) {
            newErrors.amount =
                paymentData.type === "discount"
                    ? `Скидка не может превышать остаток долга (${maxAmount} сом)`
                    : `Сумма не может превышать остаток долга (${maxAmount} сом)`;
        }

        return newErrors;
    };

    const handleSave = () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        onSave(paymentData);
        onClose();
    };

    const title =
        initialData && initialData.type === "discount"
            ? "Редактировать скидку"
            : initialData
                ? "Редактировать платеж"
                : "Добавить платеж";

    return (
        <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
            <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden">
                <div className="p-4 sm:p-5 bg-muted/30">
                    <DialogHeader className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                                <DialogTitle className="flex items-center gap-2 text-base">
                                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl border bg-background">
                                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    {title}
                                </DialogTitle>

                                <DialogDescription className="text-sm">
                                    Укажи дату и сумму. Скидка уменьшает только остаток долга.
                                </DialogDescription>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {maxAmount !== undefined ? (
                                <Badge variant="secondary" className="rounded-full">
                                    Остаток: {Number(maxAmount).toFixed(2)} сом
                                </Badge>
                            ) : null}

                            {errors.amount || errors.date ? (
                                <Badge variant="destructive" className="rounded-full">
                                    <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                                    Есть ошибки
                                </Badge>
                            ) : null}
                        </div>
                    </DialogHeader>
                </div>

                <Separator />

                <div className="p-4 sm:p-5 space-y-4">
                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground flex items-center gap-2">
                            <CreditCard className="h-4 w-4" />
                            Тип операции
                        </Label>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant={paymentData.type === "payment" ? "default" : "outline"}
                                size="sm"
                                onClick={() =>
                                    setPaymentData((prev) => ({ ...prev, type: "payment" }))
                                }
                            >
                                Платеж
                            </Button>
                            <Button
                                type="button"
                                variant={paymentData.type === "discount" ? "default" : "outline"}
                                size="sm"
                                onClick={() =>
                                    setPaymentData((prev) => ({ ...prev, type: "discount" }))
                                }
                            >
                                Скидка
                            </Button>
                        </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                <CalendarDays className="h-4 w-4" />
                                Дата платежа
                            </Label>

                            <div className="relative">
                                <Input
                                    type="date"
                                    value={paymentData.date}
                                    onChange={handleChange("date")}
                                    className={[
                                        "pr-3",
                                        errors.date ? "border-destructive focus-visible:ring-destructive" : "",
                                    ].join(" ")}
                                />
                            </div>

                            {errors.date ? (
                                <p className="text-xs text-destructive">{errors.date}</p>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    По умолчанию стоит сегодняшняя дата
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground flex items-center gap-2">
                                <Coins className="h-4 w-4" />
                                {paymentData.type === "discount" ? "Сумма скидки" : "Сумма платежа"}
                            </Label>

                            <div className="relative">
                                <Input
                                    inputMode="decimal"
                                    value={paymentData.amount}
                                    onChange={handleChange("amount")}
                                    className={[
                                        "pr-12",
                                        errors.amount ? "border-destructive focus-visible:ring-destructive" : "",
                                    ].join(" ")}
                                />
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                    сом
                                </div>
                            </div>

                            {errors.amount ? (
                                <p className="text-xs text-destructive">{errors.amount}</p>
                            ) : maxAmount !== undefined ? (
                                <p className="text-xs text-muted-foreground">
                                    Максимум: {Number(maxAmount).toFixed(2)} сом
                                </p>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    Введи сумму больше 0
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-muted-foreground">Предпросмотр</div>
                            <Badge variant="outline" className="rounded-full">
                                {paymentData.date || "—"}
                            </Badge>
                        </div>
                        <div className="mt-2 text-sm">
              <span className="text-muted-foreground">
                {paymentData.type === "discount" ? "Скидка:" : "Сумма:"}
              </span>{" "}
                            <span className="font-semibold">
                {Number(paymentData.amount || 0).toFixed(2)} сом
              </span>
                        </div>
                    </div>
                </div>

                <Separator />

                <div className="p-4 sm:p-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <Button type="button" variant="outline" onClick={onClose} className="sm:min-w-[120px]">
                        Отмена
                    </Button>
                    <Button type="button" onClick={handleSave} className="sm:min-w-[140px]">
                        <Save className="h-4 w-4 mr-2" />
                        Сохранить
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentPopup;